// Narrows the campaigns table to one marketing plan.
//
// GET /api/campaigns?brandId&planId returns the campaigns that plan
// reuses or created. Ads-summary rows stay on screen; a row stays when
// its campaignId matches campaigns[].id, or its externalId matches
// campaigns[].externalId. Arm labels come from the plan detail's
// campaigns.reused and campaigns.created (testName · side).

import { useEffect, useState } from 'react';
import {
  armLabels, getMarketingPlan, listCampaignsForPlan, listMarketingPlans, resolvePlanCampaigns,
  type PlanCampaignStatus, type PlanSummaryView
} from '../../marketingPlans/client';

type ScopeRow = {
  campaignId: string;
  externalId?: unknown;
};

type ScopeState = {
  planId: string;
  keys: string[] | null;
  ready: boolean;
  labels: Record<string, string>;
  error: string | null;
};

const IDLE: ScopeState = { planId: '', keys: null, ready: true, labels: {}, error: null };

export function useCampaignPlanScope<T extends ScopeRow>(brandId: string | null, rows: T[]) {
  const [plans, setPlans] = useState<PlanSummaryView[]>([]);
  const [plansError, setPlansError] = useState<string | null>(null);
  const [planFilter, setPlanFilter] = useState('');
  const [scope, setScope] = useState<ScopeState>(IDLE);

  useEffect(() => {
    if (!brandId) {
      setPlans([]);
      setPlansError(null);
      return;
    }
    let cancelled = false;
    void listMarketingPlans(brandId).then((result) => {
      if (cancelled) return;
      setPlans(result.plans);
      setPlansError(result.error);
    });
    return () => { cancelled = true; };
  }, [brandId]);

  useEffect(() => {
    if (!brandId || !planFilter) {
      setScope(IDLE);
      return;
    }
    let cancelled = false;
    setScope({ planId: planFilter, keys: null, ready: false, labels: {}, error: null });
    void (async () => {
      const [filtered, detail] = await Promise.all([
        listCampaignsForPlan(brandId, planFilter),
        getMarketingPlan(brandId, planFilter)
      ]);
      if (cancelled) return;
      setScope({
        planId: planFilter,
        keys: filtered.keys,
        ready: true,
        labels: detail.plan ? armLabels(detail.plan.links) : {},
        error: filtered.error
      });
    })();
    return () => { cancelled = true; };
  }, [brandId, planFilter]);

  const active = scope.planId === planFilter ? scope : { ...IDLE, planId: planFilter, ready: !planFilter };
  const resolved = resolvePlanCampaigns({
    planId: planFilter,
    rows,
    keys: active.keys,
    error: active.error,
    ready: active.ready
  });

  return {
    plans,
    plansError,
    planFilter,
    setPlanFilter,
    scopedRows: resolved.rows,
    status: resolved.status as PlanCampaignStatus,
    labels: active.labels,
    scopeError: active.error
  };
}
